import { Link } from "react-router-dom";
import { Shield, FolderOpen, Users, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EXAMPLE = `# Areon: root .env next to run.bat (one file for API + bot + music copy)
# These Discord user IDs get the web "Admin" button. Comma-separated, no spaces.
OWNER_IDS=your_discord_user_id,another_owner_id`;

export function HostAccessGuide() {
  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <p className="text-xs text-muted-foreground">
        Access only for people who are already listed. Add them by their{" "}
        <b>Discord user ID</b> (not display name).
      </p>

      <div className="space-y-2">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-7 w-7 text-primary" />
          Who can open the web /admin
        </h1>
        <p className="text-sm text-muted-foreground">
          When someone signs in with Discord, the server reads their <b>user ID</b> and
          compares it to <code>OWNER_IDS</code>. One line, multiple IDs, commas — that
          is the main path (no web form, edit the file).
        </p>
      </div>

      <Card className="p-4 sm:p-5 space-y-3">
        <h2 className="font-semibold flex items-center gap-2 text-sm">
          <Users className="h-4 w-4" />
          Multiple owners: one line, commas
        </h2>
        <pre className="text-xs bg-muted/50 border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all">
          {EXAMPLE}
        </pre>
        <p className="text-xs text-muted-foreground">
          User ID: Discord → Settings → App Settings → Advanced → Developer Mode
          On → right-click the user in chat or the member list → <b>Copy User ID</b>.
        </p>
      </Card>

      <Card className="p-4 sm:p-5 space-y-2">
        <h2 className="font-semibold flex items-center gap-2 text-sm">
          <FolderOpen className="h-4 w-4" />
          Which file
        </h2>
        <p className="text-sm text-muted-foreground">
          Areon uses one <code>.env</code> in the monorepo root (same folder as{" "}
          <code>run.bat</code>). <code>OWNER_IDS</code> is read by the Node API, not the
          browser.
        </p>
      </Card>

      <Card className="p-4 sm:p-5 space-y-2">
        <h2 className="font-semibold flex items-center gap-2 text-sm">
          <RefreshCw className="h-4 w-4" />
          After changes
        </h2>
        <p className="text-sm text-muted-foreground">
          Restart the Areon API (<code>npm run start -w apps/api</code> or rerun{" "}
          <code>run.bat</code>), then sign out and back into Discord if the admin button
          does not update.
        </p>
      </Card>

      <details className="text-sm text-muted-foreground border border-border/60 rounded-lg p-3">
        <summary className="cursor-pointer text-foreground font-medium">
          Optional: access by roles, not only user ID
        </summary>
        <p className="mt-2 pl-1">
          If you set <code>DASHBOARD_ADMIN_GUILD_ID</code> and{" "}
          <code>DASHBOARD_ADMIN_ROLE_IDS</code> in the web dashboard <code>.env</code>, members
          with those roles in that guild also get <code>/admin</code>. Values must be in
          the web <code>.env</code>; without the bot and Server Members intent this is not
          detected. If you do not need this, leave it empty; <code>OWNER_IDS</code> alone
          is enough.
        </p>
      </details>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" asChild>
          <Link to="/admin">Back to Admin</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/dashboard">Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
